import {
  LEAVING_POSITIONS as DEFAULT_POS,
  CERTIFICATE_FONT as DEFAULT_FONT,
  PAPER as DEFAULT_PAPER,
} from "../config/positions";

const ROW = { display: "flex", alignItems: "center", width: "100%", paddingLeft: "6px", paddingRight: "6px" };
const NUM = { fontSize: "11.5px", fontWeight: "600", color: "#333", minWidth: "22px", flexShrink: 0 };
const LBL = (minW = "200px") => ({ fontSize: "11.5px", color: "#333", minWidth: minW, flexShrink: 0 });

const DateDigitBoxes = ({ value = "", forPlainPaper = false, top, left, digitBoxWidth = "0.25in", font }) => {
  const digits = String(value).replace(/\D/g, "").split("");

  if (forPlainPaper) {
    return (
      <div style={{ display: "flex", gap: "2px" }}>
        {Array.from({ length: 8 }).map((_, i) => (
          <div
            key={i}
            style={{
              width: "20px",
              height: "20px",
              border: "1px solid #000",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              fontFamily: font?.family || DEFAULT_FONT.family,
              fontSize: "12px",
            }}
          >
            {digits[i] || ""}
          </div>
        ))}
      </div>
    );
  }

  return (
    <>
      {Array.from({ length: 8 }).map((_, i) => (
        <span
          key={i}
          style={{
            position: "absolute",
            top,
            left: `calc(${left} + ${i} * ${digitBoxWidth})`,
            fontFamily: font?.family || DEFAULT_FONT.family,
            fontSize: font?.size || DEFAULT_FONT.size,
            fontWeight: font?.weight || DEFAULT_FONT.weight,
            color: font?.color || DEFAULT_FONT.color,
            width: digitBoxWidth,
            textAlign: "center",
            lineHeight: 1,
          }}
        >
          {digits[i] || ""}
        </span>
      ))}
    </>
  );
};

const DigitRow = ({ value = "", boxCount, top, left, digitBoxWidth = "0.26in", forPlainPaper = false, font }) => {
  const digits = String(value).replace(/\D/g, "").split("");

  if (forPlainPaper) {
    return (
      <div style={{ display: "flex", gap: "2px" }}>
        {Array.from({ length: boxCount }).map((_, i) => (
          <div
            key={i}
            style={{
              width: "20px",
              height: "20px",
              border: "1px solid #000",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              fontFamily: font?.family || DEFAULT_FONT.family,
              fontSize: "12px",
            }}
          >
            {digits[i] || ""}
          </div>
        ))}
      </div>
    );
  }

  return (
    <>
      {digits.map((d, i) => (
        <span
          key={i}
          style={{
            position: "absolute",
            top,
            left: `calc(${left} + ${i} * ${digitBoxWidth})`,
            fontFamily: font?.family || DEFAULT_FONT.family,
            fontSize: font?.size || DEFAULT_FONT.size,
            fontWeight: font?.weight || DEFAULT_FONT.weight,
            color: font?.color || DEFAULT_FONT.color,
            width: digitBoxWidth,
            textAlign: "center",
            lineHeight: 1,
          }}
        >
          {d}
        </span>
      ))}
    </>
  );
};

function PrintedFormatOverlay({ data, previewMode, templateConfig }) {
  const POS = templateConfig?.positions || DEFAULT_POS;
  const FONT = {
    family: templateConfig?.fontFamily || DEFAULT_FONT.family,
    size: templateConfig?.fontSize || DEFAULT_FONT.size,
    weight: templateConfig?.fontWeight || DEFAULT_FONT.weight,
    color: templateConfig?.fontColor || DEFAULT_FONT.color,
  };
  const paperWidth = templateConfig?.paperWidth || DEFAULT_PAPER.leaving.width;
  const paperHeight = templateConfig?.paperHeight || DEFAULT_PAPER.leaving.height;
  const bgImage = templateConfig?.templateUrl || "/leaving-template.jpg";

  const f = (top, left, extra = {}) => ({
    position: "absolute",
    top: top || "0in",
    left: left || "0in",
    fontFamily: FONT.family,
    fontSize: FONT.size,
    fontWeight: FONT.weight,
    color: FONT.color,
    whiteSpace: "nowrap",
    lineHeight: 1,
    ...extra,
  });

  const remarks = data?.remarks || "";
  const breakAt = 82;
  const line1 = remarks.length <= breakAt ? remarks : remarks.substring(0, remarks.lastIndexOf(" ", breakAt));
  const line2 = remarks.length <= breakAt ? "" : remarks.substring(line1.length).trim();

  return (
    <div
      className="print-area"
      style={{
        position: "relative",
        width: paperWidth,
        height: paperHeight,
        overflow: "hidden",
        background: previewMode ? "#fffef5" : "transparent",
        boxShadow: previewMode ? "0 4px 24px rgba(0,0,0,0.18)" : "none",
      }}
    >
      <style>{`@media print { @page { size: ${paperWidth} ${paperHeight}; margin: 0; } }`}</style>

      {previewMode && (
        <img
          src={bgImage}
          alt="template"
          className="certificate-bg-preview"
          style={{
            position: "absolute",
            top: 0,
            left: 0,
            width: "100%",
            height: "100%",
            objectFit: "contain",
            opacity: 0.85,
            pointerEvents: "none",
          }}
        />
      )}

      <DigitRow value={data?.enrollmentNo} boxCount={16} top={POS.enrollmentNo?.top} left={POS.enrollmentNo?.left} digitBoxWidth={POS.enrollmentNo?.digitBoxWidth} font={FONT} />
      <DigitRow value={data?.aadharNo} boxCount={12} top={POS.aadharNo?.top} left={POS.aadharNo?.left} digitBoxWidth={POS.aadharNo?.digitBoxWidth} font={FONT} />
      <DateDigitBoxes value={data?.dob} top={POS.dateOfBirth?.top} left={POS.dateOfBirth?.left} digitBoxWidth={POS.dateOfBirth?.digitBoxWidth} font={FONT} />
      <DateDigitBoxes value={data?.dateOfIssue} top={POS.dateOfIssue?.top} left={POS.dateOfIssue?.left} digitBoxWidth={POS.dateOfIssue?.digitBoxWidth} font={FONT} />

      <span style={f(POS.studentName?.top, POS.studentName?.left)}>{data?.studentName}</span>
      <span style={f(POS.fatherName?.top, POS.fatherName?.left)}>{data?.fatherName}</span>
      <span style={f(POS.motherName?.top, POS.motherName?.left)}>{data?.motherName}</span>
      <span style={f(POS.nationality?.top, POS.nationality?.left)}>{data?.nationality}</span>
      <span style={f(POS.motherTongue?.top, POS.motherTongue?.left)}>{data?.motherTongue}</span>
      <span style={f(POS.religion?.top, POS.religion?.left)}>{data?.religion}</span>
      <span style={f(POS.caste?.top, POS.caste?.left)}>{data?.caste}</span>
      <span style={f(POS.subCaste?.top, POS.subCaste?.left)}>{data?.subCaste}</span>
      <span style={f(POS.placeOfBirth?.top, POS.placeOfBirth?.left)}>{data?.placeOfBirth}</span>
      <span style={f(POS.district?.top, POS.district?.left)}>{data?.district}</span>
      <span style={f(POS.state?.top, POS.state?.left)}>{data?.birthState}</span>
      <span style={f(POS.dateOfBirthInWords?.top, POS.dateOfBirthInWords?.left, { whiteSpace: "normal", maxWidth: "5in" })}>{data?.dobInWords}</span>
      <span style={f(POS.lastSchool?.top, POS.lastSchool?.left, { whiteSpace: "normal", maxWidth: "5.5in" })}>{data?.lastSchoolAttended}</span>
      <span style={f(POS.progress?.top, POS.progress?.left)}>{data?.progress}</span>
      <span style={f(POS.conduct?.top, POS.conduct?.left)}>{data?.conduct}</span>
      <span style={f(POS.dateOfAdmission?.top, POS.dateOfAdmission?.left)}>{data?.dateOfAdmission}</span>
      <span style={f(POS.stdInStudying?.top, POS.stdInStudying?.left)}>{data?.standard}</span>
      <span style={f(POS.sinceWhen?.top, POS.sinceWhen?.left)}>{data?.since}</span>
      <span style={f(POS.dateOfLeaving?.top, POS.dateOfLeaving?.left)}>{data?.dateOfLeaving}</span>
      <span style={f(POS.reasonOfLeaving?.top, POS.reasonOfLeaving?.left, { whiteSpace: "normal", maxWidth: "5.5in" })}>{data?.reasonOfLeaving}</span>
      <span style={f(POS.remarksLine1?.top || POS.remarks?.line1?.top, POS.remarksLine1?.left || POS.remarks?.line1?.left, { whiteSpace: "normal", maxWidth: "6in" })}>{line1}</span>
      {line2 && (
        <span style={f(POS.remarksLine2?.top || POS.remarks?.line2?.top, POS.remarksLine2?.left || POS.remarks?.line2?.left, { whiteSpace: "normal", maxWidth: "6in" })}>{line2}</span>
      )}
    </div>
  );
}

function PlainPaperLayout({ data, college }) {
  const VAL = {
    fontFamily: DEFAULT_FONT.family,
    fontSize: "13.5px",
    color: "#000",
    borderBottom: "1px solid #999",
    flex: 1,
    minHeight: "17px",
    paddingLeft: "3px",
  };

  const remarks = data?.remarks || "";
  const breakAt = 90;
  const line1 = remarks.length <= breakAt ? remarks : remarks.substring(0, remarks.lastIndexOf(" ", breakAt));
  const line2 = remarks.length <= breakAt ? "" : remarks.substring(line1.length).trim();

  return (
    <div
      className="print-area"
      style={{
        position: "relative",
        width: DEFAULT_PAPER.leaving.width,
        height: DEFAULT_PAPER.leaving.height,
        background: "#fff",
        boxSizing: "border-box",
      }}
    >
      <style>{`@media print { @page { size: ${DEFAULT_PAPER.leaving.width} ${DEFAULT_PAPER.leaving.height}; margin: 0; } }`}</style>
      <div style={{ position: "absolute", inset: "0.13in", border: "3px double #8B6914", borderRadius: "3px", pointerEvents: "none" }} />
      <div style={{ position: "absolute", inset: "0.20in", border: "1px solid #c9a84c", borderRadius: "2px", pointerEvents: "none" }} />

      <div style={{ position: "relative", zIndex: 1, height: "100%", display: "flex", flexDirection: "column", padding: "0.27in 0.32in" }}>
        <div style={{ textAlign: "center", borderBottom: "2px solid #8B6914", paddingBottom: "4px", flexShrink: 0 }}>
          {college?.sansthaName && (
            <div style={{ fontSize: "8.5px", fontWeight: "700", color: "#666", letterSpacing: "0.5px", textTransform: "uppercase" }}>
              {college.sansthaName}
            </div>
          )}
          <div style={{ fontSize: "17px", fontWeight: "800", color: "#1a1a1a", letterSpacing: "0.8px", lineHeight: 1.15, margin: "2px 0" }}>
            {college?.name || "CENTRAL INDIA COLLEGE OF PHARMACY"}
          </div>
          <div style={{ fontSize: "9px", fontWeight: "600", color: "#333" }}>
            {college?.address ? `${college.address}, ${college.city} - ${college.pincode}` : "Kh. No. 25/1 B, Mouza-Lonara, Nagpur - 441 111."}
          </div>
        </div>

        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", padding: "3px 0", flexShrink: 0 }}>
          <span style={{ fontSize: "11px", fontWeight: "600" }}>Serial No.&nbsp;<span>{data?.serialNo}</span></span>
          <span style={{ fontSize: "15px", fontWeight: "800", letterSpacing: "1px", textDecoration: "underline", textDecorationColor: "#8B6914" }}>Leaving Certificate</span>
          <span style={{ fontSize: "11px" }}>&nbsp;</span>
        </div>

        <div style={{ flex: 1, display: "flex", flexDirection: "column", justifyContent: "space-evenly" }}>
          <div style={ROW}><span style={LBL("168px")}>U-ID Enrollment No.</span><DigitRow value={data?.enrollmentNo} boxCount={16} forPlainPaper /></div>
          <div style={ROW}><span style={LBL("168px")}>U-ID : Aadhar No.</span><DigitRow value={data?.aadharNo} boxCount={12} forPlainPaper /></div>
          <div style={ROW}><span style={NUM}>1.</span><span style={LBL("190px")}>Name of pupil in full :</span><span style={VAL}>{data?.studentName}</span></div>
          <div style={ROW}><span style={NUM}>2.</span><span style={LBL("190px")}>Name of Father :</span><span style={VAL}>{data?.fatherName}</span></div>
          <div style={ROW}><span style={NUM}>3.</span><span style={LBL("190px")}>Name of Mother :</span><span style={VAL}>{data?.motherName}</span></div>
          <div style={ROW}><span style={NUM}>4.</span><span style={{ flexShrink: 0, marginRight: "4px" }}>Nationality :</span><span style={{ ...VAL, minWidth: "1in", maxWidth: "1.5in", marginRight: "12px" }}>{data?.nationality}</span><span style={{ flexShrink: 0, marginRight: "4px" }}>Mother Tongue :</span><span style={VAL}>{data?.motherTongue}</span></div>
          <div style={ROW}><span style={NUM}>5.</span><span style={{ flexShrink: 0, marginRight: "4px" }}>Religion :</span><span style={{ ...VAL, minWidth: "0.7in", maxWidth: "1.1in", marginRight: "10px" }}>{data?.religion}</span><span style={{ flexShrink: 0, marginRight: "4px" }}>Caste :</span><span style={{ ...VAL, minWidth: "0.7in", maxWidth: "1.1in", marginRight: "10px" }}>{data?.caste}</span><span style={{ flexShrink: 0, marginRight: "4px" }}>Sub Caste :</span><span style={VAL}>{data?.subCaste || "–"}</span></div>
          <div style={ROW}><span style={NUM}>6.</span><span style={{ flexShrink: 0, marginRight: "4px" }}>Place of Birth :</span><span style={{ ...VAL, minWidth: "0.8in", maxWidth: "1.2in", marginRight: "10px" }}>{data?.placeOfBirth}</span><span style={{ flexShrink: 0, marginRight: "4px" }}>District :</span><span style={{ ...VAL, minWidth: "0.8in", maxWidth: "1.2in", marginRight: "10px" }}>{data?.district}</span><span style={{ flexShrink: 0, marginRight: "4px" }}>State :</span><span style={VAL}>{data?.birthState}</span></div>
          <div style={ROW}><span style={NUM}>7.</span><span style={{ flexShrink: 0, marginRight: "10px" }}>Date of Birth : (DD/MM/YY)</span><DateDigitBoxes value={data?.dob} forPlainPaper /></div>
          <div style={ROW}><span style={NUM}>8.</span><span style={LBL("220px")}>Date of Birth (in Words) :</span><span style={VAL}>{data?.dobInWords}</span></div>
          <div style={ROW}><span style={NUM}>9.</span><span style={LBL("190px")}>School Last Attended :</span><span style={VAL}>{data?.lastSchoolAttended}</span></div>
          <div style={ROW}><span style={NUM}>10.</span><span style={{ flexShrink: 0, marginRight: "4px" }}>Progress :</span><span style={{ ...VAL, minWidth: "0.8in", maxWidth: "1.3in", marginRight: "12px" }}>{data?.progress}</span><span style={{ flexShrink: 0, marginRight: "4px" }}>Conduct :</span><span style={VAL}>{data?.conduct}</span></div>
          <div style={ROW}><span style={NUM}>11.</span><span style={LBL("255px")}>Date of Admission &amp; Serial No. :</span><span style={VAL}>{data?.dateOfAdmission}{data?.admissionSerialNo ? `,  ${data.admissionSerialNo}` : ""}</span></div>
          <div style={ROW}><span style={NUM}>12.</span><span style={{ flexShrink: 0, marginRight: "4px" }}>Standard in which studying :</span><span style={{ ...VAL, marginRight: "12px" }}>{data?.standard}</span><span style={{ flexShrink: 0, marginRight: "4px" }}>&amp; since when :</span><span style={{ ...VAL, minWidth: "0.5in", maxWidth: "0.8in" }}>{data?.since}</span></div>
          <div style={ROW}><span style={NUM}>13.</span><span style={LBL("200px")}>Date of Leaving College :</span><span style={VAL}>{data?.dateOfLeaving}</span></div>
          <div style={ROW}><span style={NUM}>14.</span><span style={LBL("175px")}>Reason of Leaving :</span><span style={VAL}>{data?.reasonOfLeaving}</span></div>
          <div style={{ ...ROW, alignItems: "flex-start", flexWrap: "wrap" }}><span style={NUM}>15.</span><span style={{ flexShrink: 0, marginRight: "4px" }}>Remark :</span><span style={VAL}>{line1}</span>{line2 && <div style={{ width: "100%", paddingLeft: "22px", marginTop: "1px" }}><span style={{ ...VAL, display: "block" }}>{line2}</span></div>}</div>
          <div style={ROW}><span style={NUM}>16.</span><span style={{ flexShrink: 0, marginRight: "10px" }}>Date of Issue :</span><DateDigitBoxes value={data?.dateOfIssue} forPlainPaper /></div>
        </div>
      </div>
    </div>
  );
}

export default function LeavingCertificate({ data, printMode = "printed", previewMode = false, college, templateConfig }) {
  if (printMode === "plain") return <PlainPaperLayout data={data} college={college} />;
  return <PrintedFormatOverlay data={data} previewMode={previewMode} templateConfig={templateConfig || college?.leavingTemplate} />;
}